import React, { useCallback, useEffect, useMemo, useState } from "react";
import MaterialReactTable from "material-react-table";
import axios from "axios";
import Cookies from "universal-cookie";
import Swal from "sweetalert2";
import { MRT_Localization_ES } from "material-react-table/locales/es";

import {
  FaEye,
  FaEdit,
  FaCheck,
  FaTimes,
  FaSyncAlt,
  FaBuilding,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaCalendarAlt,
  FaIdCard,
  FaSortNumericDown,
} from "react-icons/fa";

import CrearSede from "./CreaSede";

import { obtenerSedes, sedesPorInstitucionId } from "../../Services/Rest";

/* =========================================================
   CONFIGURACIÓN API
========================================================= */

const API = "https://suadesarrollo.azurewebsites.net/api";

const AUTH = {
  username: "BasicAuth@icontec.org",
  password: "yQLzqmR7eZSuAmQZUqhY",
};

/* =========================================================
   COMPONENTE
========================================================= */

function SedeAdmin({ idInstitucion }) {
  const cookies = useMemo(() => new Cookies(), []);

  const tipoUsuario = Number(cookies.get("tipo_Usuario"));
  const institucionCookie = cookies.get("institucionid");

  const esAdministrador = tipoUsuario === 1;
  const esInstitucion = tipoUsuario === 2;

  /*
   * Tanto administrador como institución
   * podrán editar la información.
   */
  const puedeEditar = esAdministrador || esInstitucion;

  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);

  const [sedeSeleccionada, setSedeSeleccionada] = useState(null);
  const [modalDetalle, setModalDetalle] = useState(false);

  /* =========================================================
     URL PARA OBTENER SEDES
  ========================================================= */

  const urlSedes = useMemo(() => {
    switch (tipoUsuario) {
      case 1:
      case 4:
        return obtenerSedes;

      case 2:
        return institucionCookie
          ? `${sedesPorInstitucionId}${institucionCookie}`
          : null;

      case 3:
        return idInstitucion
          ? `${sedesPorInstitucionId}${idInstitucion}`
          : null;

      default:
        return null;
    }
  }, [tipoUsuario, institucionCookie, idInstitucion]);

  /* =========================================================
     OBTENER SEDES
  ========================================================= */

  const cargarSedes = useCallback(async () => {
    if (!urlSedes) {
      setSedes([]);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(urlSedes, {
        auth: AUTH,
      });

      const data = Array.isArray(response.data) ? response.data : [];

      setSedes(data);
    } catch (error) {
      console.error("Error obteniendo sedes:", error);

      setSedes([]);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No fue posible obtener la información de las sedes.",
      });
    } finally {
      setLoading(false);
    }
  }, [urlSedes]);

  useEffect(() => {
    cargarSedes();
  }, [cargarSedes]);

  /* =========================================================
     VER DETALLE
  ========================================================= */

  const verDetalle = useCallback((sede) => {
    setSedeSeleccionada(sede);
    setModalDetalle(true);
  }, []);

  const cerrarDetalle = useCallback(() => {
    setModalDetalle(false);
    setSedeSeleccionada(null);
  }, []);

  /* =========================================================
     EDITAR TODA LA INFORMACIÓN DE LA SEDE
     
     PUT:
     /api/ActualizaSede?Id={id}
  ========================================================= */

  const guardarEdicion = async ({ values, row, exitEditingMode }) => {
    const sedeOriginal = row.original;

    setProcesando(true);

    try {
      /*
       * Construimos explícitamente el objeto.
       * Esto evita enviar propiedades que la API
       * no necesita.
       */

      const datosSede = {
        id: sedeOriginal.id,

        nombre: values.nombre?.trim() || "",

        direccion: values.direccion?.trim() || "",

        nombreContacto: values.nombreContacto?.trim() || "",

        numeroContacto: values.numeroContacto?.toString().trim() || "",

        institucionId: values.institucionId
          ? Number(values.institucionId)
          : sedeOriginal.institucionId,

        fechaAcreditacion: values.fechaAcreditacion || null,

        fechaRenovacion: values.fechaRenovacion || null,

        codigoPrestadorSede: values.codigoPrestadorSede?.trim() || null,

        especialidad: values.especialidad?.trim() || "",
      };

      console.log("Datos enviados a ActualizaSede:", datosSede);

      /*
       * ================================================
       * ACTUALIZAR TODA LA INFORMACIÓN
       * ================================================
       */

      await axios.put(`${API}/ActualizaSede?Id=${sedeOriginal.id}`, datosSede, {
        auth: AUTH,
      });

      /*
       * Actualizamos la tabla sin recargar la página.
       */

      setSedes((actuales) =>
        actuales.map((sede) =>
          sede.id === sedeOriginal.id
            ? {
                ...sede,
                ...datosSede,
              }
            : sede,
        ),
      );

      /*
       * Actualizamos también el detalle
       * si estaba abierto.
       */

      setSedeSeleccionada((actual) =>
        actual && actual.id === sedeOriginal.id
          ? {
              ...actual,
              ...datosSede,
            }
          : actual,
      );

      exitEditingMode();

      await Swal.fire({
        icon: "success",
        title: "Sede actualizada",
        text: "La información de la sede fue actualizada correctamente.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error actualizando sede:", error);

      console.error("Respuesta API:", error?.response?.data);

      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text:
          error?.response?.data?.message ||
          "Ocurrió un error al actualizar la información de la sede.",
      });
    } finally {
      setProcesando(false);
    }
  };

  /* =========================================================
     APROBAR / RECHAZAR SEDE

     PUT:
     /api/ActualizaEstadoSede/{sedeId}/{true|false}

     TRUE  = aprobada
     FALSE = rechazada
  ========================================================= */

  const actualizarAprobacion = async (sede, aprobada) => {
    if (!esAdministrador) {
      return;
    }

    const resultado = await Swal.fire({
      icon: aprobada ? "question" : "warning",

      title: aprobada ? "¿Aprobar sede?" : "¿Rechazar sede?",

      html: `
        <p>
          Está a punto de ${
            aprobada ? "aprobar" : "rechazar"
          } la siguiente sede:
        </p>

        <strong>
          ${sede.nombre || "Sede"}
        </strong>
      `,

      showCancelButton: true,

      confirmButtonText: aprobada ? "Sí, aprobar" : "Sí, rechazar",

      cancelButtonText: "Cancelar",

      confirmButtonColor: aprobada ? "#28a745" : "#dc3545",
    });

    if (!resultado.isConfirmed) {
      return;
    }

    setProcesando(true);

    try {
      /*
       * IMPORTANTE:
       * El endpoint recibe únicamente true o false.
       */

      await axios.put(
        `${API}/ActualizaEstadoSede/${sede.id}/${aprobada}`,
        null,
        {
          auth: AUTH,
        },
      );

      /*
       * Actualizamos tabla local.
       */

      setSedes((actuales) =>
        actuales.map((item) =>
          item.id === sede.id
            ? {
                ...item,
                aprobada,
                aprobacionRevisada: true,
              }
            : item,
        ),
      );

      /*
       * Actualizamos detalle.
       */

      setSedeSeleccionada((actual) =>
        actual && actual.id === sede.id
          ? {
              ...actual,
              aprobada,
              aprobacionRevisada: true,
            }
          : actual,
      );

      await Swal.fire({
        icon: "success",

        title: aprobada ? "Sede aprobada" : "Sede rechazada",

        text: aprobada
          ? "La sede fue aprobada correctamente."
          : "La sede fue rechazada correctamente.",

        timer: 1800,

        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error actualizando aprobación:", error);

      console.error("Respuesta API:", error?.response?.data);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No fue posible actualizar la aprobación de la sede.",
      });
    } finally {
      setProcesando(false);
    }
  };

  /* =========================================================
     COLUMNAS
  ========================================================= */

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",

        header: "ID",

        enableEditing: false,

        size: 80,
      },

      {
        accessorKey: "nombre",

        header: "Nombre de la sede",

        size: 220,

        muiEditTextFieldProps: {
          required: true,
        },
      },

      {
        accessorKey: "direccion",

        header: "Dirección",

        size: 260,

        muiEditTextFieldProps: {
          required: true,
        },
      },

      {
        accessorKey: "nombreContacto",

        header: "Persona de contacto",

        size: 200,
      },

      {
        accessorKey: "numeroContacto",

        header: "Número de contacto",

        size: 160,
      },

      {
        accessorKey: "institucionId",

        header: "Institución",

        size: 110,

        enableEditing: esAdministrador,

        muiEditTextFieldProps: {
          type: "number",
        },
      },

      {
        accessorKey: "fechaAcreditacion",

        header: "Fecha acreditación",

        size: 160,

        muiEditTextFieldProps: {
          type: "text",
          placeholder: "Ej: 2022 02 01",
        },
      },

      {
        accessorKey: "fechaRenovacion",

        header: "Fecha renovación",

        size: 160,

        muiEditTextFieldProps: {
          type: "text",
          placeholder: "Ej: 2026 01 31",
        },
      },

      {
        accessorKey: "codigoPrestadorSede",

        header: "Código prestador",

        size: 180,
      },

      {
        accessorKey: "especialidad",

        header: "Especialidad",

        size: 160,
      },

      /* =====================================================
         APROBACIÓN
      ===================================================== */

      {
        accessorKey: "aprobada",

        header: "Aprobación",

        enableEditing: false,

        size: 140,

        Cell: ({ row }) => {
          const sede = row.original;

          if (!sede.aprobacionRevisada) {
            return (
              <span className="badge badge-warning px-3 py-2">Pendiente</span>
            );
          }

          if (sede.aprobada === true) {
            return (
              <span className="badge badge-success px-3 py-2">
                <FaCheck className="mr-1" />
                Aprobada
              </span>
            );
          }

          return (
            <span className="badge badge-danger px-3 py-2">
              <FaTimes className="mr-1" />
              Rechazada
            </span>
          );
        },
      },
    ],
    [esAdministrador],
  );

  /* =========================================================
     CONTADORES
  ========================================================= */

  const total = sedes.length;

  const aprobadas = sedes.filter((sede) => sede.aprobada === true).length;

  const rechazadas = sedes.filter(
    (sede) => sede.aprobacionRevisada === true && sede.aprobada === false,
  ).length;

  const pendientes = sedes.filter((sede) => !sede.aprobacionRevisada).length;

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="container-fluid">
      {/* =====================================================
          ENCABEZADO
      ===================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="font-weight-bold text-primary mb-1">
            Gestión de Sedes
          </h4>

          <p className="text-muted mb-0">
            Administración y consulta de las sedes registradas
          </p>
        </div>

        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-outline-primary mr-2"
            onClick={cargarSedes}
            disabled={loading || procesando}
          >
            <FaSyncAlt className={`mr-2 ${loading ? "fa-spin" : ""}`} />

            {loading ? "Actualizando..." : "Actualizar"}
          </button>

          {esInstitucion && <CrearSede />}
        </div>
      </div>

      {/* =====================================================
          INDICADORES
      ===================================================== */}

      <div className="row mb-4">
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card border-left-primary shadow-sm h-100">
            <div className="card-body">
              <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                Total de sedes
              </div>

              <div className="h4 mb-0 font-weight-bold text-gray-800">
                {total}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card border-left-success shadow-sm h-100">
            <div className="card-body">
              <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                Aprobadas
              </div>

              <div className="h4 mb-0 font-weight-bold text-gray-800">
                {aprobadas}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card border-left-warning shadow-sm h-100">
            <div className="card-body">
              <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                Pendientes
              </div>

              <div className="h4 mb-0 font-weight-bold text-gray-800">
                {pendientes}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card border-left-danger shadow-sm h-100">
            <div className="card-body">
              <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                Rechazadas
              </div>

              <div className="h4 mb-0 font-weight-bold text-gray-800">
                {rechazadas}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          TABLA
      ===================================================== */}

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="m-0 font-weight-bold text-primary">
                Sedes registradas
              </h6>

              <small className="text-muted">
                {total} {total === 1 ? "registro" : "registros"}
              </small>
            </div>
          </div>
        </div>

        <div className="card-body">
          <MaterialReactTable
            localization={MRT_Localization_ES}
            columns={columns}
            data={sedes}
            state={{
              isLoading: loading,
              isSaving: procesando,
            }}
            enableGlobalFilter
            enableColumnFilters
            enableSorting
            enablePagination
            enableRowActions
            positionActionsColumn="last"
            /*
             * IMPORTANTE:
             * Ahora tanto administrador como institución
             * pueden editar la información.
             */

            enableEditing={puedeEditar}
            editingMode="modal"
            onEditingRowSave={guardarEdicion}
            renderRowActions={({ row, table }) => {
              const sede = row.original;

              return (
                <div className="d-flex">
                  {/* ================================
                      VER
                  ================================= */}

                  <button
                    type="button"
                    className="btn btn-info btn-sm mr-1"
                    title="Ver información"
                    onClick={() => verDetalle(sede)}
                  >
                    <FaEye />
                  </button>

                  {/* ================================
                      EDITAR
                  ================================= */}

                  {puedeEditar && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm mr-1"
                      title="Editar información de la sede"
                      disabled={procesando}
                      onClick={() => table.setEditingRow(row)}
                    >
                      <FaEdit />
                    </button>
                  )}

                  {/* ================================
                      APROBAR
                  ================================= */}

                  {esAdministrador && (
                    <button
                      type="button"
                      className="btn btn-success btn-sm mr-1"
                      title="Aprobar sede"
                      disabled={procesando}
                      onClick={() => actualizarAprobacion(sede, true)}
                    >
                      <FaCheck />
                    </button>
                  )}

                  {/* ================================
                      RECHAZAR
                  ================================= */}

                  {esAdministrador && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      title="Rechazar sede"
                      disabled={procesando}
                      onClick={() => actualizarAprobacion(sede, false)}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              );
            }}
            initialState={{
              density: "comfortable",

              pagination: {
                pageIndex: 0,
                pageSize: 10,
              },

              columnVisibility: {
                id: false,
                institucionId: false,
              },
            }}
            muiTablePaperProps={{
              elevation: 0,

              sx: {
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                overflow: "hidden",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                fontWeight: 700,
                fontSize: "0.85rem",
              },
            }}
            muiTableBodyRowProps={{
              hover: true,
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: "0.88rem",
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 430px)",
              },
            }}
          />
        </div>
      </div>

      {/* =====================================================
          MODAL DETALLE
      ===================================================== */}

      {modalDetalle && sedeSeleccionada && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{
            backgroundColor: "rgba(0,0,0,0.55)",
          }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            role="document"
          >
            <div className="modal-content border-0 shadow-lg">
              {/* HEADER */}

              <div className="modal-header bg-primary text-white">
                <div>
                  <h5 className="modal-title font-weight-bold">
                    Información de la sede
                  </h5>

                  <small>Detalle completo del registro</small>
                </div>

                <button
                  type="button"
                  className="close text-white"
                  onClick={cerrarDetalle}
                >
                  <span>&times;</span>
                </button>
              </div>

              {/* BODY */}

              <div className="modal-body">
                <div className="row">
                  {/* NOMBRE */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaBuilding className="text-primary mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Nombre de la sede
                        </small>

                        <strong>
                          {sedeSeleccionada.nombre || "No registrado"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* ID */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaIdCard className="text-primary mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">ID</small>

                        <strong>{sedeSeleccionada.id}</strong>
                      </div>
                    </div>
                  </div>

                  {/* DIRECCIÓN */}

                  <div className="col-md-12 mb-4">
                    <div className="d-flex">
                      <FaMapMarkerAlt className="text-danger mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">Dirección</small>

                        <strong>
                          {sedeSeleccionada.direccion || "No registrada"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* CONTACTO */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaUser className="text-primary mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Persona de contacto
                        </small>

                        <strong>
                          {sedeSeleccionada.nombreContacto || "No registrado"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* TELÉFONO */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaPhone className="text-success mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Número de contacto
                        </small>

                        <strong>
                          {sedeSeleccionada.numeroContacto || "No registrado"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* ACREDITACIÓN */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaCalendarAlt className="text-primary mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Fecha de acreditación
                        </small>

                        <strong>
                          {sedeSeleccionada.fechaAcreditacion ||
                            "No registrada"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* RENOVACIÓN */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaCalendarAlt className="text-warning mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Fecha de renovación
                        </small>

                        <strong>
                          {sedeSeleccionada.fechaRenovacion || "No registrada"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* CÓDIGO */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaSortNumericDown className="text-warning mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Código prestador
                        </small>

                        <strong>
                          {sedeSeleccionada.codigoPrestadorSede ||
                            "No registrado"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* ESPECIALIDAD */}

                  <div className="col-md-6 mb-4">
                    <div className="d-flex">
                      <FaIdCard className="text-primary mr-3 mt-1" />

                      <div>
                        <small className="text-muted d-block">
                          Especialidad
                        </small>

                        <strong>
                          {sedeSeleccionada.especialidad || "No registrada"}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                {/* APROBACIÓN */}

                <div>
                  <small className="text-muted d-block mb-2">
                    Estado de aprobación
                  </small>

                  {!sedeSeleccionada.aprobacionRevisada ? (
                    <span className="badge badge-warning px-3 py-2">
                      Pendiente de revisión
                    </span>
                  ) : sedeSeleccionada.aprobada ? (
                    <span className="badge badge-success px-3 py-2">
                      <FaCheck className="mr-1" />
                      Aprobada
                    </span>
                  ) : (
                    <span className="badge badge-danger px-3 py-2">
                      <FaTimes className="mr-1" />
                      Rechazada
                    </span>
                  )}
                </div>

                {/* JUSTIFICACIÓN */}

                {sedeSeleccionada.justificacion && (
                  <>
                    <hr />

                    <div>
                      <small className="text-muted d-block mb-2">
                        Justificación
                      </small>

                      <div className="bg-light p-3 rounded">
                        {sedeSeleccionada.justificacion}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* FOOTER */}

              <div className="modal-footer">
                {esAdministrador && (
                  <>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={procesando}
                      onClick={() =>
                        actualizarAprobacion(sedeSeleccionada, false)
                      }
                    >
                      <FaTimes className="mr-2" />
                      Rechazar
                    </button>

                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={procesando}
                      onClick={() =>
                        actualizarAprobacion(sedeSeleccionada, true)
                      }
                    >
                      <FaCheck className="mr-2" />
                      Aprobar
                    </button>
                  </>
                )}

                {puedeEditar && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      cerrarDetalle();

                      /*
                       * Buscamos la fila en la tabla
                       * mediante el DOM no es necesario.
                       *
                       * Por simplicidad el usuario puede
                       * utilizar el botón Editar de la tabla.
                       */
                    }}
                  >
                    <FaEdit className="mr-2" />
                    Cerrar y editar desde la tabla
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cerrarDetalle}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SedeAdmin;
